import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const MenuContext = createContext();

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};

export const MenuProvider = ({ children }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);

  useEffect(() => {
    loadMenuCategories();
    loadMenuItems();
  }, []);

  const loadMenuCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setMenuCategories(data || []);
    } catch (error) {
      console.error('Error loading menu categories:', error);
    }
  };

  const loadMenuItems = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .select(`
          *,
          menu_categories(name, icon)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      const formattedItems = (data || []).map(item => ({
        ...item,
        category: item.category_id,
        locationIds: item.location_ids || [],
        price: parseFloat(item.price) || 0
      }));
      
      setMenuItems(formattedItems);
    } catch (error) {
      console.error('Error loading menu items:', error);
    }
  };

  const addMenuItem = async (item) => {
    try {
      const { data, error } = await supabase
        .from('menu_items')
        .insert([{
          name: item.name,
          category_id: item.category,
          price: item.price,
          image: item.image,
          description: item.description,
          ingredients: item.ingredients,
          customizable: item.customizable,
          options: item.options,
          location_ids: item.locationIds
        }])
        .select()
        .single();

      if (error) throw error;
      
      await loadMenuItems();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to add menu item' };
    }
  };

  const updateMenuItem = async (itemId, updatedItem) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .update({
          name: updatedItem.name,
          category_id: updatedItem.category,
          price: updatedItem.price,
          image: updatedItem.image,
          description: updatedItem.description,
          ingredients: updatedItem.ingredients,
          customizable: updatedItem.customizable,
          options: updatedItem.options,
          location_ids: updatedItem.locationIds,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;
      
      await loadMenuItems();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update menu item' };
    }
  };

  const deleteMenuItem = async (itemId) => {
    try {
      const { error } = await supabase
        .from('menu_items')
        .update({ is_active: false })
        .eq('id', itemId);

      if (error) throw error;
      
      await loadMenuItems();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete menu item' };
    }
  };

  const duplicateMenuItem = async (itemId) => {
    try {
      const originalItem = menuItems.find(item => item.id === itemId);
      if (!originalItem) {
        return { success: false, error: 'Item not found' };
      }

      const { data, error } = await supabase
        .from('menu_items')
        .insert([{
          name: `${originalItem.name} (Copy)`,
          category_id: originalItem.category_id,
          price: originalItem.price,
          image: originalItem.image,
          description: originalItem.description,
          ingredients: originalItem.ingredients,
          customizable: originalItem.customizable,
          options: originalItem.options,
          location_ids: originalItem.location_ids
        }])
        .select()
        .single();

      if (error) throw error;
      
      await loadMenuItems();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to duplicate menu item' };
    }
  };

  const copyMenuItems = async (itemIds, targetLocationIds) => {
    try {
      const itemsToCopy = menuItems.filter(item => itemIds.includes(item.id));
      
      for (const item of itemsToCopy) {
        // Create new location_ids array by merging existing with target locations
        const newLocationIds = [...new Set([...(item.location_ids || []), ...targetLocationIds])];
        
        await supabase
          .from('menu_items')
          .update({
            location_ids: newLocationIds,
            updated_at: new Date().toISOString()
          })
          .eq('id', item.id);
      }
      
      await loadMenuItems();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to copy menu items' };
    }
  };

  const addCategory = async (category) => {
    try {
      const { data, error } = await supabase
        .from('menu_categories')
        .insert([{
          name: category.name,
          icon: category.icon,
          sort_order: category.sortOrder
        }])
        .select()
        .single();

      if (error) throw error;
      
      await loadMenuCategories();
      return { success: true };
    } catch (error) {
      if (error.code === '23505') {
        return { success: false, error: 'A category with this name already exists' };
      }
      return { success: false, error: 'Failed to add category' };
    }
  };

  const updateCategory = async (categoryId, updatedCategory) => {
    try {
      const { error } = await supabase
        .from('menu_categories')
        .update({
          name: updatedCategory.name,
          icon: updatedCategory.icon,
          sort_order: updatedCategory.sortOrder,
          updated_at: new Date().toISOString()
        })
        .eq('id', categoryId);

      if (error) throw error;
      
      await loadMenuCategories();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update category' };
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      // Check if there are items in this category
      const { data: items } = await supabase
        .from('menu_items')
        .select('id')
        .eq('category_id', categoryId)
        .eq('is_active', true);

      if (items && items.length > 0) {
        return { success: false, error: 'Cannot delete category with existing items' };
      }

      const { error } = await supabase
        .from('menu_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
      
      await loadMenuCategories();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to delete category' };
    }
  };

  const getItemsByCategory = (categoryId) => {
    return menuItems.filter(item => item.category_id === categoryId || item.category === categoryId);
  };

  const getItemsByLocation = (locationId) => {
    return menuItems.filter(item => 
      !item.location_ids || 
      item.location_ids.length === 0 || 
      item.location_ids.includes(locationId)
    );
  };

  const getItemsByCategoryAndLocation = (categoryId, locationId) => {
    return menuItems.filter(item => 
      (item.category_id === categoryId || item.category === categoryId) &&
      (!item.location_ids || 
       item.location_ids.length === 0 || 
       item.location_ids.includes(locationId))
    );
  };

  return (
    <MenuContext.Provider value={{
      menuItems,
      menuCategories,
      addMenuItem,
      updateMenuItem,
      deleteMenuItem,
      duplicateMenuItem,
      copyMenuItems,
      addCategory,
      updateCategory,
      deleteCategory,
      getItemsByCategory,
      getItemsByLocation,
      getItemsByCategoryAndLocation
    }}>
      {children}
    </MenuContext.Provider>
  );
};